<script setup lang="ts">
import { computed } from 'vue'
import { AltArrowDownIcon } from '@solar-icons/vue/linear'
import { useDisclosure } from '../../../composables/useDisclosure'
import { vCollapsible } from '../v-collapsible'

const props = withDefaults(
  defineProps<{
    /** Initial open state when uncontrolled. Ignored once `open` is supplied. */
    defaultOpen?: boolean
    /** Controlled open state; leave `undefined` to let the primitive own it. */
    open?: boolean
  }>(),
  {
    defaultOpen: false,
    // Required: an absent Boolean prop casts to `false`, reading as controlled closed.
    open: undefined,
  },
)

const emit = defineEmits<{
  'update:open': [open: boolean]
}>()

const controlledOpen = computed(() => props.open === true)

const { isOpen, toggle, triggerAttrs, contentAttrs } = useDisclosure({
  defaultOpen: props.defaultOpen,
  open: props.open === undefined ? undefined : controlledOpen,
  onOpenChange: (next) => emit('update:open', next),
})
</script>

<template>
  <div class="flex flex-col">
    <button
      type="button"
      v-bind="triggerAttrs"
      :class="[
        'flex w-full cursor-pointer items-center gap-2 rounded-2 text-left',
        'outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-blue',
      ]"
      @click="toggle"
    >
      <span class="flex min-w-0 grow items-center gap-2"><slot name="summary" /></span>
      <AltArrowDownIcon
        aria-hidden="true"
        :class="[
          'shrink-0 text-icon-tertiary transition-transform duration-base ease-soft motion-reduce:transition-none',
          isOpen && 'rotate-180',
        ]"
      />
    </button>

    <div v-collapsible="isOpen" class="collapsible">
      <div>
        <div v-bind="contentAttrs" class="pt-4"><slot /></div>
      </div>
    </div>
  </div>
</template>
